import { Link, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { ShoppingCart, User, LogOut, Menu, X, ChevronDown, ChevronRight } from 'lucide-react'
import { useCartStore } from '@/stores/cartStore'
import { useState, useEffect } from 'react'
import { WelcomeTour } from '@/components/WelcomeTour'

// Navigation items with dropdowns
const navigationItems = [
  { name: 'Shop', path: '/shop' },
  {
    name: 'SIM Only',
    path: '/sim-only',
    allLabel: 'All Sims',
    dropdown: [
      { name: 'Data Only Sim', path: '/sim-only/data-only' },
      { name: 'Calls, Text & Data Sim', path: '/sim-only/calls-text-data' },
      { name: 'Multi-Network Sim', path: '/sim-only/multisim' },
      { name: 'Roaming Sims', path: '/sim-only/roaming' },
      { name: 'GuardData SIM', path: '/sim-only/guarddata-sim' },
      { name: 'LongLife SIM', path: '/sim-only/longlife-sim' },
    ],
  },
  {
    name: 'Roaming',
    path: '/roaming',
    dropdown: [
      { name: 'Euro MultiNet', path: '/roaming/multinet' },
      { name: 'Go Roam Rates & Info', path: '/three-go-roam/rates' },
      { name: 'Go Roam Destinations', path: '/three-go-roam/destinations' },
      // TEMPORARILY HIDDEN — uncomment to re-enable World MultiNet zones in nav
      // { name: 'World MultiNet Zone 1', path: '/roaming/zone1' },
      // { name: 'World MultiNet Zone 2', path: '/roaming/zone2' },
      // { name: 'World MultiNet Zone 3', path: '/roaming/zone3' },
      // { name: 'World MultiNet Zone 4', path: '/roaming/zone4' },
      // { name: 'World MultiNet Zone 5', path: '/roaming/zone5' },
      // { name: 'World MultiNet Zone 6', path: '/roaming/zone6' },
    ],
  },
  {
    name: 'Broadband',
    path: '/broadband',
    dropdown: [
      { name: 'Rolling Monthly (1 Month)', path: '/rolling-monthly-broadband' },
      { name: 'Starlink Satellite', path: '/broadband/starlink' },
      { name: 'Premium Broadband', path: '/broadband/premium' },
      { name: 'Lite Broadband', path: '/broadband/lite' },
      { name: 'Mifi Portable', path: '/broadband/mifi' },
    ],
  },
  { name: 'Bundles', path: '/bundles' },
  // { name: 'Free SIM', path: '/free-sim' }, // Temporarily removed - uncomment to re-enable
  { name: 'Landline', path: '/voip' },
  { name: 'FAQ', path: '/faq' },
  { name: 'Coverage', path: '/network-coverage' },
  { name: 'Special Offers', path: '/special-offers' },
]

export default function WebsiteLayout() {
  const { isAuthenticated, user, logout } = useAuthStore()
  const { getItemCount } = useCartStore()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState<string | null>(null)

  // The full-viewport tiled background belongs to the home page ONLY. Control it
  // here at the layout level keyed on the route, so every page deterministically
  // sets (home) or clears (everything else) the body background. Previously only
  // Home set/cleared it, which meant prerendered non-home pages baked in the home
  // background and a hard refresh kept it until you navigated away. (See Home.tsx.)
  useEffect(() => {
    const body = document.body
    if (location.pathname === '/') {
      body.style.backgroundImage = "url('/images/fine_green_lines.png')"
      body.style.backgroundSize = '100vw 100vh'
      body.style.backgroundPosition = 'top center'
      body.style.backgroundRepeat = 'repeat'
    } else {
      body.style.backgroundImage = ''
      body.style.backgroundSize = ''
      body.style.backgroundPosition = ''
      body.style.backgroundRepeat = ''
    }
    return () => {
      body.style.backgroundImage = ''
      body.style.backgroundSize = ''
      body.style.backgroundPosition = ''
      body.style.backgroundRepeat = ''
    }
  }, [location.pathname])

  const handleLogout = () => {
    logout()
    window.location.href = '/'
  }

  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
    setMobileExpandedMenu(null)
  }

  const toggleMobileSubmenu = (menuName: string) => {
    setMobileExpandedMenu(mobileExpandedMenu === menuName ? null : menuName)
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm overflow-visible">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between overflow-visible">
          <Link to="/" className="hover:scale-105 transition-transform">
            <img src="/silux_logo.png" alt="Silux Mobile" className="h-14 sm:h-16 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4">
            {navigationItems.map((item) =>
              item.dropdown ? (
                <div
                  key={item.name}
                  className="relative group"
                  onMouseEnter={() => setOpenDropdown(item.name)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="flex items-center gap-0.5 text-gray-700 hover:text-primary-600 font-medium transition-colors py-2 text-sm lg:text-base whitespace-nowrap">
                    {item.name}
                    <ChevronDown className={`w-3.5 h-3.5 lg:w-4 lg:h-4 transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                  </button>
                  {/* Dropdown Menu */}
                  <div
                    className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                      openDropdown === item.name
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    <div className="w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden">
                      <Link
                        to={item.path}
                        onClick={() => setOpenDropdown(null)}
                        className="block px-4 py-2.5 text-gray-700 hover:text-primary-600 hover:bg-primary-50 font-medium transition-colors"
                      >
                        {item.allLabel || `All ${item.name}`}
                      </Link>
                      <div className="border-t border-gray-100 my-1" />
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setOpenDropdown(null)}
                          className="block px-4 py-2.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className="text-gray-700 hover:text-primary-600 font-medium transition-colors py-2 text-sm lg:text-base whitespace-nowrap"
                >
                  {item.name}
                </Link>
              )
            )}

            <div className="flex items-center gap-2 lg:gap-4 ml-2 lg:ml-4 pl-2 lg:pl-4 border-l">
              <Link
                to="/cart"
                className="relative text-gray-700 hover:text-primary-600"
              >
                <ShoppingCart className="w-5 h-5" />
                {getItemCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {getItemCount()}
                  </span>
                )}
              </Link>

              {isAuthenticated ? (
                <>
                  <Link
                    to={user?.is_admin ? '/admin' : '/account'}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-all"
                  >
                    <User className="w-5 h-5" />
                    <span className="text-sm font-medium">{user?.first_name}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-gray-700 hover:text-red-600 hover:bg-red-50 transition-all"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-5 py-2 rounded-xl hover:shadow-lg hover:shadow-primary-500/50 transition-all font-semibold hover:-translate-y-0.5"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Icons */}
          <div className="flex md:hidden items-center gap-3">
            <Link
              to="/cart"
              className="relative text-gray-700 hover:text-primary-600"
            >
              <ShoppingCart className="w-5 h-5" />
              {getItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getItemCount()}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </nav>

      </header>

      {/* Mobile Menu Drawer — must be outside <header> because backdrop-blur creates a
          new containing block that traps position:fixed children inside the 80px header */}
      {mobileMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={closeMobileMenu}
          />

          {/* Drawer */}
          <div className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 shadow-2xl overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <span className="text-lg font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
                Menu
              </span>
              <button
                onClick={closeMobileMenu}
                className="p-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 space-y-1">
              {/* Navigation Links */}
              {navigationItems.map((item) =>
                item.dropdown ? (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMobileSubmenu(item.name)}
                      className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-all"
                    >
                      <span>{item.name}</span>
                      <ChevronRight
                        className={`w-5 h-5 transition-transform ${
                          mobileExpandedMenu === item.name ? 'rotate-90' : ''
                        }`}
                      />
                    </button>
                    {mobileExpandedMenu === item.name && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-primary-100">
                        <Link
                          to={item.path}
                          onClick={closeMobileMenu}
                          className="block px-4 py-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-r-lg transition-all"
                        >
                          {item.allLabel || `All ${item.name}`}
                        </Link>
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={closeMobileMenu}
                            className="block px-4 py-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-r-lg transition-all"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={closeMobileMenu}
                    className="block px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-all"
                  >
                    {item.name}
                  </Link>
                )
              )}

              {/* Divider */}
              <div className="border-t border-gray-200 my-4" />

              {/* User Actions */}
              {isAuthenticated ? (
                <>
                  <Link
                    to={user?.is_admin ? '/admin' : '/account'}
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-all"
                  >
                    <User className="w-5 h-5" />
                    <span>My Account ({user?.first_name})</span>
                  </Link>
                  <button
                    onClick={() => {
                      closeMobileMenu()
                      handleLogout()
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-all"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-all"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeMobileMenu}
                    className="block bg-gradient-to-r from-primary-600 to-primary-500 text-white px-4 py-3 rounded-lg hover:shadow-lg font-semibold transition-all text-center"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <main>
        <Outlet />
      </main>

      <WelcomeTour />
    </div>
  )
}
